// API base URL. Set at Vite build time via the VITE_API_URL env var.
// - Dev (this repo, docker compose up): http://localhost:3000 (api on a
//   different port; CORS allows it on the api side).
// - Prod: the public origin (e.g. https://mirabili.example.com) when the
//   DO droplet's Traefik does path-based routing; or http://localhost:3000
//   if the prod is also split-port like dev.
const BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
  }
}

interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as T;
  // Read the body as text first so we can both inspect it and parse safely.
  const raw = await res.text();
  const contentType = res.headers.get('Content-Type') ?? '';
  let data: unknown = raw;
  if (contentType.includes('application/json') && raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      // Fall back to the raw text if JSON parsing fails.
      data = raw;
    }
  }
  if (!res.ok) {
    const msg = (data && typeof data === 'object' && 'error' in data && typeof (data as { error: unknown }).error === 'string')
      ? (data as { error: string }).error
      : `HTTP ${res.status}`;
    // eslint-disable-next-line no-console
    console.error('[api]', opts.method ?? 'GET', path, '→', res.status, msg, data);
    throw new ApiError(res.status, data, msg);
  }
  return data as T;
}
